import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ConfirmationService} from 'primeng/api';
import {ObjectBaseComponent} from '../../../../../core/shared/object/mvs-object-base/object-base.component';
import {NavigationItem} from '../../../../../core/shared/dto/navigation/navigation-item';
import {WidgetData} from '../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../core/shared/widget/service/widget.factory';
import {FilterCriteria} from '../../../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../../../core/shared/misc/sorting';
import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../core/shared/service/message/mvs-message.service';
import {ObserverService} from '../../../../../core/shared/object/service/observer.service';
import {SpSprintDto} from '../../../model/dto/entity/sp-sprint/sp-sprint.dto';
import {SpSprintService} from '../../../service/api/sp-sprint/sp-sprint.service';
import {SpMcpService} from '../../../service/domain/sp-mcp/sp-mcp.service';

@Component({
    selector: 'mvs-sp-sprint-object-component',
    templateUrl: './sp-sprint-object.component.html',
    standalone: false
})
export class SpSprintObjectComponent extends ObjectBaseComponent implements OnInit, OnChanges, OnDestroy {

    sprintDataWidget: WidgetData;
    ticketWidget: WidgetData;

    sprintDto: SpSprintDto;
    sprintBusy: boolean = false;

    constructor(
        protected coreService: MvsCoreService,
        protected messageService: MvsMessageService,
        protected confirmationService: ConfirmationService,
        protected observerService: ObserverService,
        protected sprintService: SpSprintService,
        protected spMcpService: SpMcpService
    ) {
        super(coreService, messageService, confirmationService, observerService);
    }

    ngOnInit(): void {
        super.ngOnInit();
    }

    onObjectChanged() {
        this.setupNavigationItems();
        this.refreshComponent();
    }

    refreshComponent() {
        if (!this.objectIdentifier) {
            return;
        }

        this.loadSprintData();
        this.loadWidgets();
        this.initialized = true;
    }

    setupNavigationItems() {
        const navItems: NavigationItem[] = [
            {
                label: 'Overview',
                action: 'overview',
                tooltip: 'Sprint Overview',
                icon: 'fa-regular fa-house',
                toggleable: false,
                default: true
            },
            {
                label: 'Tickets',
                action: 'tickets',
                tooltip: 'Sprint Tickets',
                icon: 'fa-regular fa-ticket',
                toggleable: false,
                default: false
            }
        ];

        this.onNavigationItems.emit(navItems);
    }

    loadSprintData() {
        this.sprintService.get(this.objectIdentifier.objectId).subscribe({
            next: (dto: SpSprintDto) => {
                this.sprintDto = dto;
                this.onPageTitle.emit(dto.name || 'Sprint');
            }
        });
    }

    loadWidgets() {
        const sprintId = this.objectIdentifier.objectId;

        this.sprintDataWidget = WidgetFactory.createWidgetEntityData(
            'sp.sprint.object.detail.data',
            'Sprint Details',
            'sp.SpSprint',
            sprintId
        );

        this.ticketWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'sp.sprint.object.ticket.table',
            'Sprint Tickets',
            'sp.SpProjectTicket',
            [FilterCriteria.create('e.sprintId', FilterCriteria.cOperatorEqual, sprintId)],
            [new Sorting('e.priority', true)]
        );
    }

    handleStartSprint() {
        if (!this.sprintDto) {
            return;
        }
        this.sprintBusy = true;
        this.spMcpService.startSprint(this.objectIdentifier.objectId).subscribe({
            next: () => {
                this.messageService.showSuccessMessage('Sprint', 'Sprint started.');
                this.sprintBusy = false;
                this.loadSprintData();
                this.loadWidgets();
            },
            error: () => {
                this.messageService.showErrorMessage('Sprint', 'Failed to start sprint.');
                this.sprintBusy = false;
            }
        });
    }

    handleCompleteSprint() {
        if (!this.sprintDto) {
            return;
        }
        this.sprintBusy = true;
        this.spMcpService.completeSprint(this.objectIdentifier.objectId).subscribe({
            next: () => {
                this.messageService.showSuccessMessage('Sprint', 'Sprint completed.');
                this.sprintBusy = false;
                this.loadSprintData();
                this.loadWidgets();
            },
            error: () => {
                this.messageService.showErrorMessage('Sprint', 'Failed to complete sprint.');
                this.sprintBusy = false;
            }
        });
    }

    getSprintStatusSeverity(): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
        if (!this.sprintDto) return 'info';
        switch (this.sprintDto.status) {
            case 0: return 'secondary';
            case 1: return 'info';
            case 2: return 'success';
            case 3: return 'danger';
            default: return 'info';
        }
    }

    getSprintStatusLabel(): string {
        if (!this.sprintDto) return '';
        switch (this.sprintDto.status) {
            case 0: return 'Planned';
            case 1: return 'Active';
            case 2: return 'Completed';
            case 3: return 'Cancelled';
            default: return 'Unknown';
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['objectIdentifier']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
    }
}
